/**
 * Type-safe API client — cross-target (web/Vite/Tauri).
 *
 * Port of ``ando-frontend/app/api/apiFetch.ts`` (341 LOC v1.x) with the
 * Next.js / SSR-specific bits removed:
 *
 *   - No ``headers()`` or ``next/server`` imports — works in any fetch env.
 *   - No ``window.location.href`` redirects — the host app registers an
 *     ``onAuthFailure`` callback via ``registerAuthFailureHandler()``
 *     (see ``tokenRefresh.ts``).
 *   - Base URL resolved lazily from ``getEnv().apiUrl`` so the runtime
 *     config injected by Docker (``window.__ANDO_RUNTIME__``) is honoured.
 *
 * Behaviour preserved from v1:
 *   - ``credentials: 'include'`` for BFF httpOnly-cookie auth.
 *   - Request timeout via ``AbortController`` (default 120 s, opt-out per request).
 *   - Caller cancellation remains distinct from a request timeout.
 *   - Automatic 401 → refresh-once → retry, with a single global refresh
 *     in-flight to prevent thundering-herd.
 *   - 429 ``Retry-After`` → ``RateLimitError``.
 *   - Structured ``ApiError`` for all other non-2xx with parsed JSON body.
 */

import { getDeviceId } from "../auth/fingerprint.js";
import { getEnv } from "../env.js";
import { ApiError, AuthError, NetworkError, RateLimitError, SchemaError } from "./errors.js";
import { emitSessionGate, isAuthEndpoint, refreshAccessToken } from "./tokenRefresh.js";

/**
 * Minimal structural type for a zod parser. We DON'T import zod here so that
 * callers using a different zod major (e.g. apps/web pins v3, packages/core
 * pins v4) still typecheck — only the parse contract matters.
 */
export interface SchemaParser<T> {
  safeParse(
    input: unknown,
  ): { success: true; data: T } | { success: false; error: { issues: unknown[] } };
}

const DEFAULT_TIMEOUT_MS = 120_000;

export interface ApiClientConfig {
  /**
   * Explicit base URL. If omitted, ``getEnv().apiUrl`` is read lazily on
   * each request — this is what you want for the Docker single-image
   * pattern (runtime-config.js loaded after bundle eval).
   */
  baseUrl?: string;
  headers?: HeadersInit;
  /** Per-instance timeout override (ms). Default 120 000. */
  timeout?: number;
}

export interface RequestConfig extends RequestInit {
  /** Request timeout in ms; null explicitly disables the local timer. */
  timeout?: number | null;
  /**
   * Optional zod parser run against the parsed JSON body. On failure a
   * ``SchemaError`` is thrown with the raw ``issues`` array. Use this to
   * harden the BE/FE contract at the boundary — feature slices stay
   * typed without `as unknown as T` casts.
   */
  schema?: SchemaParser<unknown>;
  /**
   * Optional ``Idempotency-Key`` header value. Mirrors v1
   * (``BaseSaaSConnectorClient.IDEMPOTENCY_HEADER_SUPPORTED``) — opt-in per
   * call. Stripe / SF Bulk 2.0 honour it; SF REST + FiC reject it.
   */
  idempotencyKey?: string;
  /** @internal Prevents infinite retry loops on 401. */
  _isRetryAfterRefresh?: boolean;
}

/** Minimal subset used in PUT/POST/PATCH typed bodies. */
type JsonBody = unknown;

/**
 * Auth token provider — caller registers a callback that returns the
 * current Bearer access token (or ``null`` when the user isn't logged
 * in). The web BFF path leaves this unregistered; the Tauri desktop
 * shell wires it to ``getDesktopAccessToken()`` which reads from the
 * OS keychain (Rust ``keyring``) secrets store.
 *
 * Why a provider (vs storing the token on the client): avoids stale
 * captures — every request re-reads the freshest token, so the silent
 * refresh path (``useDesktopAuth.refresh``) takes effect on the very
 * next request without a re-init.
 */
export type AuthTokenProvider = () => Promise<string | null> | string | null;

/**
 * Process-wide fallback Bearer provider, shared by EVERY ``ApiClient``
 * instance that has no instance-level provider of its own.
 *
 * Why: the desktop shell registers the OS-keychain token provider once at
 * boot. Registering it only on the product singleton (``apiClient``) left
 * every other instance — the Platform Console client is bound to the
 * ``/platform`` mount and MUST be a separate instance — sending no
 * ``Authorization`` header, so the console 401'd on desktop while the
 * cookie-backed web build worked (Sep 4, 2026). One registration, all
 * transports.
 */
let defaultAuthTokenProvider: AuthTokenProvider | null = null;

export function setDefaultAuthTokenProvider(provider: AuthTokenProvider | null): void {
  defaultAuthTokenProvider = provider;
}

export class ApiClient {
  private readonly baseUrlProvider: () => string;
  private readonly defaultHeaders: HeadersInit;
  private readonly timeout: number;
  private instanceAuthTokenProvider: AuthTokenProvider | null = null;

  /** Instance provider wins; otherwise the process-wide default. */
  private get authTokenProvider(): AuthTokenProvider | null {
    return this.instanceAuthTokenProvider ?? defaultAuthTokenProvider;
  }

  constructor(config: ApiClientConfig = {}) {
    const explicit = config.baseUrl;
    this.baseUrlProvider = explicit !== undefined ? () => explicit : () => getEnv().apiUrl;
    this.defaultHeaders = config.headers ?? {};
    this.timeout = config.timeout ?? DEFAULT_TIMEOUT_MS;
  }

  /**
   * Register the source of truth for the ``Authorization: Bearer …``
   * header. Called once at app bootstrap by the desktop shell
   * (``apps/desktop`` flow); the web BFF flow leaves it unset.
   *
   * Passing ``null`` clears the provider — used on sign-out so the
   * next request 401s cleanly rather than continuing with a stale
   * token.
   */
  setAuthTokenProvider(provider: AuthTokenProvider | null): void {
    this.instanceAuthTokenProvider = provider;
  }

  /**
   * Resolve the current Bearer access token, if one is registered.
   *
   * Helper for transports that need to mirror the ``Authorization``
   * header behaviour of ``request()`` without round-tripping through
   * fetch — most notably the XHR-based multipart upload path which
   * needs native progress events. Mirrors the silent best-effort
   * contract: returns ``null`` on any failure / when no provider is
   * registered (web BFF flow), and a string otherwise.
   */
  async getAuthToken(): Promise<string | null> {
    if (!this.authTokenProvider) return null;
    try {
      return (await this.authTokenProvider()) ?? null;
    } catch {
      return null;
    }
  }

  /** Resolve the base URL the client is currently bound to. */
  getBaseUrl(): string {
    return this.baseUrlProvider();
  }

  /**
   * Core request method — handles URL building, timeout, error mapping
   * and 401 → refresh → retry.
   *
   * Public surface for callers that need to bypass the JSON helpers
   * (e.g. multipart `FormData` uploads which must let the browser set
   * the Content-Type boundary). High-level call sites should prefer
   * `get` / `post` / `patch` / `put` / `delete`.
   */
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
      timeout = this.timeout,
      _isRetryAfterRefresh = false,
      schema,
      idempotencyKey,
      ...fetchConfig
    } = config;

    const baseUrl = this.baseUrlProvider();
    const url =
      endpoint.startsWith("http") || endpoint.startsWith(baseUrl)
        ? endpoint
        : `${baseUrl}${endpoint}`;

    // Merge default headers with per-request headers.
    const headers = new Headers(this.defaultHeaders);
    if (fetchConfig.headers) {
      const requestHeaders = new Headers(fetchConfig.headers);
      requestHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }

    // Device fingerprint header — preserved from v1 for device-bound session
    // validation (Zitadel-style). Best-effort: if the fingerprint module fails
    // (e.g. SSR context), we proceed without the header rather than abort.
    if (!headers.has("X-Device-Id")) {
      try {
        const deviceId = await getDeviceId();
        if (deviceId) headers.set("X-Device-Id", deviceId);
      } catch {
        // Fingerprint best-effort; never block the request.
      }
    }

    // Idempotency-Key header — opt-in per call (see MEMORY rule:
    // "Idempotency-Key HTTP header è opt-in nei SaaS connector").
    if (idempotencyKey && !headers.has("Idempotency-Key")) {
      headers.set("Idempotency-Key", idempotencyKey);
    }

    // Authorization: Bearer <access_token> — desktop / native flow.
    // The web BFF doesn't register a provider (auth rides on the
    // httpOnly ``ando_session`` cookie); Tauri does. Caller-set
    // Authorization always wins (e.g. the desktop-exchange POST that
    // hasn't yet stored a token).
    if (!headers.has("Authorization") && this.authTokenProvider) {
      try {
        const token = await this.authTokenProvider();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // Token provider best-effort — never block the request.
      }
    }

    const cancellation = createRequestCancellation(timeout, fetchConfig.signal ?? undefined);
    const { signal } = cancellation;

    try {
      let response: Response;
      try {
        response = await fetch(url, {
          ...fetchConfig,
          headers,
          signal,
          credentials: "include",
        });
      } catch (err) {
        cancellation.throwIfAborted(err);
        throw new NetworkError(err instanceof Error ? err.message : "Network error", err);
      } finally {
        cancellation.stopTimer();
      }

      if (response.ok) {
        const body = await parseSuccessBody<T>(response);
        if (!schema) return body;
        const result = schema.safeParse(body);
        if (result.success) return result.data as T;
        throw new SchemaError(
          `Response schema validation failed for ${endpoint}`,
          result.error.issues,
          response,
          body,
        );
      }

      // --- 401 Unauthorized: refresh-once then retry ---
      if (
        response.status === 401 &&
        !_isRetryAfterRefresh &&
        !isAuthEndpoint(endpoint) &&
        typeof window !== "undefined"
      ) {
        throwIfCallerAborted(fetchConfig.signal);
        try {
          await refreshAccessToken();
          throwIfCallerAborted(fetchConfig.signal);
          return await this.request<T>(endpoint, { ...config, _isRetryAfterRefresh: true });
        } catch (err) {
          if (isAbortError(err)) throw err;
          throwIfCallerAborted(fetchConfig.signal);
          // fall through to the AuthError throw below
        }
      }

      const { parsedBody, rawText } = await readErrorBody(response);
      const detail = pickDetail(parsedBody);

      // Engine wallet recovery. Keep transport generic: emit only a semantic
      // browser event and let the Console decide how to render the CTA.
      if (response.status === 402 && typeof window !== "undefined") {
        const body = parsedBody as {
          error_code?: unknown;
          error?: { code?: unknown } | unknown;
        } | null;
        const nestedCode =
          body?.error && typeof body.error === "object"
            ? (body.error as { code?: unknown }).code
            : undefined;
        const code =
          typeof body?.error_code === "string"
            ? body.error_code
            : typeof nestedCode === "string"
              ? nestedCode
              : "";
        const normalized = code.toLowerCase();
        if (
          normalized === "insufficient_credits" ||
          normalized === "credit_exhausted" ||
          normalized === "wallet_empty"
        ) {
          window.dispatchEvent(new CustomEvent("ando:engine-wallet-empty"));
        }
      }

      if (response.status === 401) {
        throw new AuthError(detail ?? "Unauthorized", response, parsedBody ?? rawText);
      }

      // --- 403 session-gate resync (port of v1 userAuth.ts:390-415) ---
      // The BFF returns 403 when the session is technically valid but a
      // session-level gate fires (account deactivated, subscription expired).
      // Detail vocabulary is BE-stable: emit a structured reason for the host
      // app to act on (cookie patch + reload in web, route push in Tauri).
      // Match BOTH the string-detail form (v1) and the structured ``error_code``
      // form (forward-compat with the FastAPI ``AppError`` payload).
      if (response.status === 403) {
        const code = parsedBody?.error_code;
        const detailLower = typeof detail === "string" ? detail.toLowerCase() : "";
        if (code === "ACCOUNT_INACTIVE" || detailLower.includes("account inactive")) {
          emitSessionGate("account_inactive");
        } else if (
          code === "SUBSCRIPTION_REQUIRED" ||
          detailLower.includes("subscription required")
        ) {
          emitSessionGate("subscription_required");
        }
        // Fall through to the generic ApiError throw — the host handler does
        // not retry; callers see a 403 ApiError as before.
      }

      if (response.status === 429) {
        const retryAfterHeader = response.headers.get("Retry-After");
        const parsed = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : Number.NaN;
        const retryAfter = Number.isFinite(parsed) ? parsed : 60;
        throw new RateLimitError(
          detail ?? "Too many requests",
          retryAfter,
          response,
          parsedBody ?? rawText,
        );
      }

      const message = detail ?? `HTTP ${response.status}: ${response.statusText}`;
      throw new ApiError(message, response.status, response, parsedBody ?? rawText);
    } finally {
      cancellation.dispose();
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, { ...config, method: "GET" });
  }

  /**
   * Like :meth:`request` but resolves the success body as a ``Blob``
   * instead of trying to JSON-decode it. Used for authenticated
   * downloads (email attachments, exports) where we can't expose a
   * naked ``<a href>`` to the route because the API client carries
   * its bearer token in a header rather than a cookie.
   *
   * 401 refresh-and-retry is preserved by going through ``fetch``
   * directly with the same header-merging plumbing as ``request``;
   * the rest of the error mapping (403/429/etc.) is identical. The
   * method is intentionally lean — no schema validation, no idempotency
   * key — because none of those apply to binary downloads.
   */
  async requestBlob(endpoint: string, config: RequestConfig = {}): Promise<Blob> {
    const { timeout = this.timeout, _isRetryAfterRefresh = false, ...fetchConfig } = config;

    const baseUrl = this.baseUrlProvider();
    const url =
      endpoint.startsWith("http") || endpoint.startsWith(baseUrl)
        ? endpoint
        : `${baseUrl}${endpoint}`;

    const headers = new Headers(this.defaultHeaders);
    if (fetchConfig.headers) {
      const requestHeaders = new Headers(fetchConfig.headers);
      requestHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }
    if (!headers.has("X-Device-Id")) {
      try {
        const deviceId = await getDeviceId();
        if (deviceId) headers.set("X-Device-Id", deviceId);
      } catch {
        // best-effort
      }
    }
    if (!headers.has("Authorization") && this.authTokenProvider) {
      try {
        const token = await this.authTokenProvider();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // best-effort
      }
    }

    const cancellation = createRequestCancellation(timeout, fetchConfig.signal ?? undefined);
    const { signal } = cancellation;

    try {
      let response: Response;
      try {
        response = await fetch(url, {
          ...fetchConfig,
          headers,
          signal,
          credentials: "include",
        });
      } catch (err) {
        cancellation.throwIfAborted(err);
        throw new NetworkError(err instanceof Error ? err.message : "Network error", err);
      } finally {
        cancellation.stopTimer();
      }

      if (response.ok) {
        return await response.blob();
      }

      // 401 → refresh-once → retry — same one-shot policy as ``request``.
      if (
        response.status === 401 &&
        !_isRetryAfterRefresh &&
        !isAuthEndpoint(endpoint) &&
        typeof window !== "undefined"
      ) {
        throwIfCallerAborted(fetchConfig.signal);
        try {
          await refreshAccessToken();
          throwIfCallerAborted(fetchConfig.signal);
          return await this.requestBlob(endpoint, {
            ...config,
            _isRetryAfterRefresh: true,
          });
        } catch (err) {
          if (isAbortError(err)) throw err;
          throwIfCallerAborted(fetchConfig.signal);
          // fall through to AuthError below
        }
      }

      const { parsedBody, rawText } = await readErrorBody(response);
      const detail = pickDetail(parsedBody);
      if (response.status === 401) {
        throw new AuthError(detail ?? "Unauthorized", response, parsedBody ?? rawText);
      }
      const message = detail ?? `HTTP ${response.status}: ${response.statusText}`;
      throw new ApiError(message, response.status, response, parsedBody ?? rawText);
    } finally {
      cancellation.dispose();
    }
  }

  async post<T>(endpoint: string, data?: JsonBody, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, {
      ...config,
      method: "POST",
      headers: { "Content-Type": "application/json", ...(config?.headers ?? {}) },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * POST a ``FormData`` (multipart/form-data upload).
   *
   * ``post()`` cannot be used for uploads because it forces
   * ``Content-Type: application/json`` and ``JSON.stringify(FormData)``
   * returns ``"{}"`` — the body arrives empty on the server and FastAPI
   * rejects with ``Field required (field: body.<name>)``.
   *
   * This helper mirrors ``.post()`` but preserves the multipart body:
   *   - No ``Content-Type`` header — ``fetch`` auto-sets it with the
   *     mandatory ``boundary=…`` parameter that FastAPI's ``UploadFile``
   *     parser needs.
   *   - Body passed through verbatim.
   *
   * Extracted Jul 22, 2026 after the DFM checklist upload failed with the
   * exact "field required" signature the guard above was written against.
   * Consolidates the inline ``apiClient.request({ method:"POST", body:form })``
   * pattern that had been copy-pasted across five endpoint files.
   */
  async postMultipart<T>(endpoint: string, form: FormData, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, {
      ...config,
      method: "POST",
      body: form,
    });
  }

  async put<T>(endpoint: string, data?: JsonBody, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, {
      ...config,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...(config?.headers ?? {}) },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: JsonBody, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, {
      ...config,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...(config?.headers ?? {}) },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return await this.request<T>(endpoint, { ...config, method: "DELETE" });
  }

  /**
   * Fetch binary bytes (PDF / DOCX / XLSX / image) through the same
   * auth pipeline as the JSON methods — ``Authorization: Bearer …``
   * from the registered token provider + ``credentials: include`` for
   * the cookie-based web path. Returns an ``ArrayBuffer`` (no JSON
   * parse); throws ``ApiError`` / ``AuthError`` on non-2xx.
   *
   * Use for editor primitives (Docx, Xlsx, PDF, PPTX preview) that
   * download bytes from the canonical proxy
   * (``/api/v1/documents/{doc_id}/file`` or
   * ``/api/v1/documents/{doc_id}/preview``). Raw ``fetch()`` would
   * bypass the bearer-token logic, leaking 401 on every Tauri build
   * (SameSite=Lax cookie blocked cross-origin from
   * ``tauri://localhost``).
   */
  /**
   * Fetch a binary download and return both the ``Blob`` and the raw
   * ``Headers`` so callers can lift the suggested filename out of
   * ``Content-Disposition`` (RFC 6266). Same auth + retry pipeline as
   * the JSON methods.
   *
   * Used by the BOM Excel exporter and any future xlsx/csv/pdf streaming
   * endpoint that needs the BE-suggested filename rather than picking
   * one client-side.
   */
  async getBlobWithHeaders(
    endpoint: string,
    config?: RequestConfig,
  ): Promise<{ blob: Blob; headers: Headers }> {
    const {
      schema: _schema,
      timeout = this.timeout,
      _isRetryAfterRefresh,
      ...fetchConfig
    } = config ?? {};
    void _schema;
    void _isRetryAfterRefresh;

    const baseUrl = this.baseUrlProvider();
    const url =
      endpoint.startsWith("http") || endpoint.startsWith(baseUrl)
        ? endpoint
        : `${baseUrl}${endpoint}`;

    const headers = new Headers(this.defaultHeaders);
    if (fetchConfig.headers) {
      const reqHeaders = new Headers(fetchConfig.headers);
      reqHeaders.forEach((v, k) => headers.set(k, v));
    }
    if (!headers.has("X-Device-Id")) {
      try {
        const deviceId = await getDeviceId();
        if (deviceId) headers.set("X-Device-Id", deviceId);
      } catch {
        // best-effort
      }
    }
    if (!headers.has("Authorization") && this.authTokenProvider) {
      try {
        const token = await this.authTokenProvider();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // best-effort
      }
    }

    const cancellation = createRequestCancellation(timeout, fetchConfig.signal ?? undefined);
    const { signal } = cancellation;

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        method: "GET",
        headers,
        signal,
        credentials: "include",
      });
      if (!response.ok) {
        const { parsedBody, rawText } = await readErrorBody(response);
        const detail = pickDetail(parsedBody);
        if (response.status === 401) {
          throw new AuthError(detail ?? "Unauthorized", response, parsedBody ?? rawText);
        }
        throw new ApiError(
          detail ?? `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          response,
          parsedBody ?? rawText,
        );
      }
      const blob = await response.blob();
      return { blob, headers: response.headers };
    } catch (err) {
      cancellation.throwIfAborted(err);
      if (err instanceof ApiError || err instanceof AuthError) throw err;
      throw new NetworkError(err instanceof Error ? err.message : "Network error", err);
    } finally {
      cancellation.dispose();
    }
  }

  async getBytes(endpoint: string, config?: RequestConfig): Promise<ArrayBuffer> {
    const {
      schema: _schema,
      timeout = this.timeout,
      _isRetryAfterRefresh,
      ...fetchConfig
    } = config ?? {};
    void _schema;
    void _isRetryAfterRefresh;

    const baseUrl = this.baseUrlProvider();
    const url =
      endpoint.startsWith("http") || endpoint.startsWith(baseUrl)
        ? endpoint
        : `${baseUrl}${endpoint}`;

    const headers = new Headers(this.defaultHeaders);
    if (fetchConfig.headers) {
      const reqHeaders = new Headers(fetchConfig.headers);
      reqHeaders.forEach((v, k) => headers.set(k, v));
    }
    if (!headers.has("X-Device-Id")) {
      try {
        const deviceId = await getDeviceId();
        if (deviceId) headers.set("X-Device-Id", deviceId);
      } catch {
        // best-effort
      }
    }
    if (!headers.has("Authorization") && this.authTokenProvider) {
      try {
        const token = await this.authTokenProvider();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // best-effort
      }
    }

    const cancellation = createRequestCancellation(timeout, fetchConfig.signal ?? undefined);
    const { signal } = cancellation;

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        method: "GET",
        headers,
        signal,
        credentials: "include",
      });
      if (!response.ok) {
        const { parsedBody, rawText } = await readErrorBody(response);
        const detail = pickDetail(parsedBody);
        if (response.status === 401) {
          throw new AuthError(detail ?? "Unauthorized", response, parsedBody ?? rawText);
        }
        throw new ApiError(
          detail ?? `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          response,
          parsedBody ?? rawText,
        );
      }
      return await response.arrayBuffer();
    } catch (err) {
      cancellation.throwIfAborted(err);
      if (err instanceof ApiError || err instanceof AuthError) throw err;
      throw new NetworkError(err instanceof Error ? err.message : "Network error", err);
    } finally {
      cancellation.dispose();
    }
  }
}

/** One cancellation owner for every request path; the first abort wins. */
function createRequestCancellation(timeout: number | null, callerSignal: AbortSignal | undefined) {
  const controller = new AbortController();
  let abortedBy: "caller" | "timeout" | undefined;
  const abort = (owner: "caller" | "timeout"): void => {
    if (abortedBy !== undefined) return;
    abortedBy = owner;
    controller.abort();
  };
  const onCallerAbort = (): void => abort("caller");
  if (callerSignal?.aborted) onCallerAbort();
  else callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
  const timeoutId = timeout === null ? undefined : setTimeout(() => abort("timeout"), timeout);
  const stopTimer = (): void => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  };

  return {
    signal: controller.signal,
    stopTimer,
    dispose(): void {
      stopTimer();
      callerSignal?.removeEventListener("abort", onCallerAbort);
    },
    throwIfAborted(error: unknown): void {
      if (abortedBy === "timeout") {
        throw new ApiError(`Request timeout after ${timeout}ms`, 408);
      }
      if (isAbortError(error)) throw error;
      if (abortedBy === "caller") throw new DOMException("The request was aborted.", "AbortError");
    },
  };
}

function isAbortError(error: unknown): error is Error {
  return error instanceof Error && error.name === "AbortError";
}

function throwIfCallerAborted(signal: AbortSignal | null | undefined): void {
  if (signal?.aborted) throw new DOMException("The request was aborted.", "AbortError");
}

async function parseSuccessBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return {} as T;
  }
  const text = await response.text();
  if (text.length === 0) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // Non-JSON 2xx — return the raw text cast to T (caller knows the shape).
    return text as unknown as T;
  }
}

async function readErrorBody(
  response: Response,
): Promise<{ parsedBody: Record<string, unknown> | null; rawText: string }> {
  let rawText = "";
  try {
    rawText = await response.text();
  } catch {
    // body unreadable — treat as empty
  }
  let parsedBody: Record<string, unknown> | null = null;
  try {
    if (rawText.length > 0) {
      const candidate = JSON.parse(rawText);
      if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
        parsedBody = candidate as Record<string, unknown>;
      }
    }
  } catch {
    // not JSON
  }
  return { parsedBody, rawText };
}

function pickDetail(body: Record<string, unknown> | null): string | null {
  if (!body) return null;
  // FastAPI carries either a plain string ``detail`` or a structured one
  // (``{code, message, ...}``) when the endpoint needs machine-readable fields
  // alongside the human sentence. Reading ``.message`` off the object keeps the
  // explanation instead of degrading to "HTTP 422" — the caller can still
  // inspect ``ApiError.data`` for the structured part.
  const detail = body.detail;
  const candidates: unknown[] = [
    detail,
    detail && typeof detail === "object" ? (detail as Record<string, unknown>).message : undefined,
    body.message,
    body.error,
  ];
  for (const v of candidates) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

/** Default singleton — uses ``getEnv().apiUrl`` lazily. */
export const apiClient = new ApiClient();
